import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { TeamDetailData } from '../types/teamsDetail.types';

export const teamsDetailService = {
  async getTeamDetail(id: string, userId: string): Promise<TeamDetailData> {
    const { data: teamData, error: tError } = await supabase
      .from('teams')
      .select('company_id')
      .eq('id', id)
      .single();
    
    if (tError) throw tError;
    
    await permissionEngine.assert(userId, 'teams.view', { companyId: teamData.company_id, teamId: id });

    const { data, error } = await supabase
      .from('teams')
      .select(`
        *,
        project:projects!project_id(id, name),
        leader:user_profiles!leader_user_id(id, full_name, avatar_path),
        vice_leader:user_profiles!vice_leader_user_id(id, full_name, avatar_path)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    // Fetch members
    const { data: members, error: mError } = await supabase
      .from('team_members')
      .select(`
        *,
        user:user_profiles(id, full_name, avatar_path, role_code)
      `)
      .eq('team_id', id)
      .eq('is_active', true);

    if (mError) throw mError;

    // Mocking some data for the complex UI components as per image
    return {
      ...data,
      leader: data.leader ? {
        id: data.leader.id,
        full_name: data.leader.full_name,
        avatar_url: data.leader.avatar_path
      } : undefined,
      vice_leader: data.vice_leader ? {
        id: data.vice_leader.id,
        full_name: data.vice_leader.full_name,
        avatar_url: data.vice_leader.avatar_path
      } : undefined,
      member_count: members?.length || 0,
      project_count: 5,
      active_work_orders: 18,
      performance_avg: 92,
      founded_date: data.created_at,
      zone_name: 'Khu A',
      
      members: (members || []).map((m, i) => ({
        id: m.id,
        team_role_id: m.member_role || 'member',
        joined_at: m.created_at,
        user: {
          id: m.user?.id || '',
          full_name: m.user?.full_name || 'Thành viên',
          avatar_url: m.user?.avatar_path,
          email: m.user?.email || '',
          role_name: m.user?.role_code || 'Thành viên'
        },
        contribution_percent: 95 - (i * 3) > 0 ? 95 - (i * 3) : 50
      })),

      projects: [
        { id: '1', name: 'Khu đô thị Green City', role: 'Thi công chính', zone: 'Khu A', start_date: '01/06/2024', status_label: 'Đang thực hiện', status_variant: 'success' },
        { id: '2', name: 'Công viên trung tâm', role: 'Thi công chính', zone: 'Khu A', start_date: '15/06/2024', status_label: 'Đang thực hiện', status_variant: 'success' },
        { id: '3', name: 'Khu biệt thự sinh thái', role: 'Thi công chính', zone: 'Khu A', start_date: '20/07/2024', status_label: 'Chuẩn bị', status_variant: 'info' },
        { id: '4', name: 'Đường nội khu A-03', role: 'Thi công chính', zone: 'Khu A', start_date: '05/08/2024', status_label: 'Đang thực hiện', status_variant: 'success' },
        { id: '5', name: 'Cảnh quan hồ điều hòa', role: 'Thi công chính', zone: 'Khu A', start_date: '18/08/2024', status_label: 'Chuẩn bị', status_variant: 'info' },
      ],

      activities: [
        { id: '1', user: { full_name: 'Phạm Văn Tài' }, action: 'đã cập nhật thông tin team', timestamp: '22/05/2024 10:30' },
        { id: '2', user: { full_name: 'Nguyễn Văn An' }, action: 'được thêm vào team', timestamp: '22/05/2024 09:15' },
        { id: '3', user: { full_name: 'Hệ thống' }, action: 'Đội thi công số 1 được tạo mới', timestamp: '22/05/2024 08:45' },
      ]
    } as TeamDetailData;
  },

  async addMembers(teamId: string, userIds: string[], role: string) {
    const payload = userIds.map(userId => ({
      team_id: teamId,
      user_id: userId,
      member_role: role
    }));
    const { error } = await supabase.from('team_members').insert(payload);
    if (error) throw error;
  },

  async removeMember(memberId: string) {
    const { error } = await supabase.from('team_members').delete().eq('id', memberId);
    if (error) throw error;
  },

  async updateMemberRole(memberId: string, role: string) {
    const { error } = await supabase.from('team_members').update({ member_role: role }).eq('id', memberId);
    if (error) throw error;
  },

  async getAvailableUsers(companyId: string, teamId: string) {
    const { data: teamMembers } = await supabase.from('team_members').select('user_id').eq('team_id', teamId);
    const excludeIds = (teamMembers || []).map(m => m.user_id);
    
    let query = supabase.from('user_profiles').select('id, full_name, email, role_code').eq('company_id', companyId);
    if (excludeIds.length > 0) {
      query = query.not('id', 'in', `(${excludeIds.join(',')})`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }
};
